import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import * as stylex from "@stylexjs/stylex";
import { accessAPI, errorMessage, queries } from "../lib/api";
import { styles } from "../lib/styles";
import { AccessTable } from "../components/AccessTable";
import { ServerForm } from "../components/ServerForm";
import { Drawer, PageHeader } from "../components/Workspace";

export const Route = createFileRoute("/servers/")({ component: ServersPage });
function ServersPage() {
  const me = useQuery(queries.me());
  const state = useQuery({ ...queries.state(), enabled: me.data?.admin === true });
  const [adding, setAdding] = useState(false);
  const cache = useQueryClient();
  const sync = useMutation({
    mutationFn: accessAPI.reconcile,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["access"] }),
  });
  if (!me.data?.admin)
    return (
      <p {...stylex.props(styles.notice)}>Administrator access is required to manage servers.</p>
    );
  return (
    <>
      <PageHeader
        title="Servers"
        description="Manage server accounts and see whether access is ready."
        action={
          <>
            <Button label="Synchronize" onClick={() => sync.mutate()} isLoading={sync.isPending} />
            <Button label="Add server" variant="primary" onClick={() => setAdding(true)} />
          </>
        }
      />
      {(state.error || sync.error) && (
        <p role="alert" {...stylex.props(styles.error)}>
          {errorMessage(state.error || sync.error)}
        </p>
      )}
      {sync.isSuccess && (
        <output {...stylex.props(styles.notice)}>
          Synchronization queued. Applied access will update after verification.
        </output>
      )}
      {state.isPending && (
        <p aria-busy="true" {...stylex.props(styles.notice)}>
          Loading servers…
        </p>
      )}
      {state.data && (
        <>
          <div className="table-toolbar">
            <div className="inline-health">
              <span
                className={`health-dot${state.data.sync_error || !state.data.last_sync ? " unhealthy" : ""}`}
              />
              {state.data.sync_error
                ? "Synchronization needs attention"
                : state.data.last_sync
                  ? `Synced ${new Date(state.data.last_sync).toLocaleString()}`
                  : "Awaiting first synchronization"}
            </div>
            <span {...stylex.props(styles.small)}>
              {state.data.servers.length} registered accounts
            </span>
          </div>
          {state.data.sync_error && (
            <p role="alert" {...stylex.props(styles.error)}>
              {state.data.sync_error}
            </p>
          )}
          <section data-workspace-panel {...stylex.props(styles.panel)}>
            <AccessTable state={state.data} />
          </section>
        </>
      )}
      {adding && (
        <Drawer
          title="Add server"
          description="Register an existing NetBird peer for managed SSH access."
          onClose={() => setAdding(false)}
        >
          <ServerForm onSaved={() => setAdding(false)} />
        </Drawer>
      )}
    </>
  );
}
